import { createColumnHelper } from "@tanstack/react-table";
import Table from "components/ui/Table";
import { TableBreakpoint } from "components/ui/Table/interface";
import getYear from "date-fns/getYear";
import { NativeSelect, palette } from "lib/miolo";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";
import { useMemo, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import type { IAmericanIssuerSpecialDividendsCorporateAction } from "types/graphqlTypes";

interface IAmericanSpecialDividendsPaidTabContetProps {
  data?: IAmericanIssuerSpecialDividendsCorporateAction[];
  loading: boolean;
}

export default function AmericanSpecialDividendsPaidTabContet({
  data,
  loading,
}: IAmericanSpecialDividendsPaidTabContetProps) {
  const [filterSpecialDividendsByYear, setFilterSpecialDividendsByYear] = useState<number>();
  const columnHelper = createColumnHelper<IAmericanIssuerSpecialDividendsCorporateAction>();
  const availableYears = useMemo(
    () => [...new Set(data?.map((dividend) => getYear(parseDateYmd(dividend.excludingDate))))],
    [data],
  );

  if (!data && !filterSpecialDividendsByYear) {
    return null;
  }

  if (loading) {
    return <ClipLoader size={28} color={palette.royalBlue} loading={true} />;
  }

  const arrayOfColumns = [
    columnHelper.accessor("excludingDate", {
      header: "Excluding Date",
      cell: (props) => formatDateOrReturnHyphen(props.getValue()),
    }),
    columnHelper.accessor("payDate", {
      header: "Pay Date",
      cell: (props) => formatDateOrReturnHyphen(props.getValue()),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
    columnHelper.accessor("amountPaid", {
      header: "Value ($)",
      cell: (props) => numeral(props.getValue()).format("0.00").replace(".", ","),
      meta: {
        breakpoint: TableBreakpoint.Small,
      },
    }),
  ];

  const arrayOfDataFilteredByYear = data?.filter((dividend) => {
    if (!filterSpecialDividendsByYear) {
      return true;
    }

    if (getYear(parseDateYmd(dividend.recordDate)) === filterSpecialDividendsByYear) {
      return true;
    }

    return false;
  });

  return (
    <div className="flex flex-col items-end space-y-2">
      <div className="w-52">
        <NativeSelect
          height={48}
          value={filterSpecialDividendsByYear}
          onChange={(event) => setFilterSpecialDividendsByYear(Number(event.target.value))}
          style={{
            borderRadius: "8px",
          }}
        >
          <option value={undefined}>Filter By Year</option>
          {availableYears.map((year) => (
            <option key={`selectBoxYear/${year}`} value={year}>
              {year}
            </option>
          ))}
        </NativeSelect>
      </div>

      <Table<IAmericanIssuerSpecialDividendsCorporateAction>
        columns={arrayOfColumns}
        data={arrayOfDataFilteredByYear ?? []}
        isOddEven={true}
        isPaginationEnabled={true}
      />
    </div>
  );
}
